// Lokalna wersja ProductItem dla sekcji "Możesz potrzebować" - układ poziomy
// @ts-nocheck

import React, { useState, FC, useMemo, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useSelector } from 'store';
import {
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement,
  usePostProductQuantity
} from 'api';
import { IProductListItem, IUnit } from 'api/types';
import { AddToCartButton, AddToShoppingListButton } from 'components/containers';
import { Availability, Counter, Link } from 'components/controls';
import { AvailabilitySource, buildAvailabilityLabel } from 'components/controls/Availability/Availability';
import WiseSelect from 'components/wise/WiseSelect';
import { useProductWithLabels, hasSpecialPriceLabel } from 'utils/productHelpers';
import LabelsList from 'components/controls/LabelsList';

import styles from './ProductItem.module.scss';

interface Props {
  product: IProductListItem;
  categoryId?: number;
  searchKeywords?: string;
  slide?: boolean;
}

const mapStockLabelForAvailability = (
  product: IProductListItem,
  availablePrefix: string
): IProductListItem => {
  const stock = product.product_stock_formatted_short_info;
  if (!stock) return product;
  const source: AvailabilitySource = {
    product_price_short_info: product.product_price_short_info,
    product_unit_short_info: product.product_unit_short_info
  };
  const label = buildAvailabilityLabel(stock, source, product.product_unit_short_info?.units, availablePrefix);
  if (label === stock.name) return product;

  return {
    ...product,
    product_stock_formatted_short_info: {
      ...stock,
      name: label
    }
  };
};

const ProductItem: FC<Props> = ({ product, categoryId, searchKeywords, slide }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { profile } = useSelector((state) => state.auth);

  const productWithLabels = useProductWithLabels(product);

  // aktualna ilość
  const [quantity, setQuantity] = useState(1);

  // aktualnie zmieniona ilość (sprzed walidacji)
  const [manuallyChangedQuantity, setManuallyChangedQuantity] = useState<number>();


  // ID aktualnej jednostki
  const [unitId, setUnitId] = useState<number>(
    product.product_unit_short_info?.default_unit_id || 
    product.product_price_short_info?.prices?.[0]?.unit_id || 
    0
  );

  // aktualna jednostka
  const price = useMemo(
    () => product?.product_price_short_info?.prices?.find((price) => price.unit_id === unitId),
    [unitId, product]
  );

  // uaktualnienie jednostki
  useEffect(() => {
    if (product.product_unit_short_info?.default_unit_id) {
      setUnitId(product.product_unit_short_info?.default_unit_id);
    } else if (product.product_price_short_info?.prices?.[0]?.unit_id) {
      setUnitId(product.product_price_short_info.prices[0].unit_id);
    }
  }, [product.product_unit_short_info?.default_unit_id, product.product_price_short_info?.prices]);

  // zmiana ilości po zmianie jednostki
  useEffect(() => {
    price &&
      setQuantity(
        product.product_unit_short_info?.units?.find((unit) => unitId === unit.unit_id)
          ?.minimal_quantity || 1
      );
  }, [price, unitId, product]);

  // pobranie informacji o zmniejszonej ilości
  const { mutate: decrementQuantity, isLoading: isDecrementQuantityLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: incrementQuantity, isLoading: isIncrementQuantityLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji ilości
  const {
    mutate: updateQuantity,
    mutateAsync: updateQuantityAsync,
    isLoading: isUpdateQuantityLoading
  } = usePostProductQuantity(product.id, {
    onSuccess: (data) => {
      setQuantity(data.data.value);
    }
  });

  // funkcja renderująca dropdown jednostek (tylko dla zalogowanych)
  const renderUnitSelector = useCallback(() => {
    if (profile?.role === 'ROLE_OPEN_PROFILE') return null;
    const unit = product.product_unit_short_info?.units?.find((unit) => unitId === unit.unit_id);
    if (!product.product_unit_short_info?.units || product.product_unit_short_info.units.length <= 1) {
      return null;
    }
    return (
      <div className={styles.unitSelectorWrapper}>
        <WiseSelect<IUnit>
          onChange={(unit) => unit?.unit_id && setUnitId(unit.unit_id)}
          value={unitId}
          styleGroup="portal"
          options={product.product_unit_short_info?.units.map((unit) => ({
            value: unit.unit_id,
            label: (
              <div className={styles.selectLabelWrapper}>
                <div className={styles.selectLabelMain}>{unit.name}</div>
                <div className={styles.selectLabelSub}>
                  {unit.converter !== 1 ? `(${unit.converter})` : ''}
                </div>
              </div>
            ),
            item: unit
          }))}
        />
      </div>
    );
  }, [unitId, product, profile?.role]);

  const productWithAvailabilityLabel = useMemo(
    () => mapStockLabelForAvailability(product, t('Dostępne')),
    [product, t]
  );

  if (!slide) {
    // Jeśli nie jest slide, używamy core ProductItem bezpośrednio
    const ProductItemCore = require('@monorepo/core/src/components/containers/ProductItem/ProductItem').default;
    const hasPriceLabel = product.product_price_short_info?.prices?.some(hasSpecialPriceLabel);
    return (
      <div className={classnames({ [styles.priceWithLabelWrapper]: hasPriceLabel })}>
        <ProductItemCore
          product={productWithAvailabilityLabel}
          categoryId={categoryId}
          searchKeywords={searchKeywords}
          slide={slide}
        />
      </div>
    );
  }

  const hasPriceLabel = hasSpecialPriceLabel(price);

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Containers-ProductItem',
        { [styles.priceWithLabel]: hasPriceLabel }
      )}
      itemProp="item"
      itemScope
      itemType="http://schema.org/Product">
      <LabelsList
        labels={productWithLabels.product_basic_short_info?.labels || []}
        className={styles.labelsWrapper}
      />
      <div className={styles.imageWrapper}>
        <Link
          to={`/${product.product_basic_short_info?.url_link}`}
          state={{ categoryId, searchKeywords }}
          className={styles.productImage}
          style={{
            backgroundImage: `url(${product.product_image_formatted_short_info?.images?.[0]?.min})`
          }}>
        </Link>
        <meta
          itemProp="image"
          content={product.product_image_formatted_short_info?.images?.[0]?.min}
        />
      </div>

      <div className={styles.productDetails}>
        <div className={styles.productTitle}>
          <Link
            className={styles.title}
            to={`/${product.product_basic_short_info?.url_link}`}
            state={{ categoryId, searchKeywords }}>
            <span itemProp="name">{product.product_basic_short_info?.title}</span>
          </Link>
          <meta
            itemProp="url"
            content={window.location.origin + product.product_basic_short_info?.url_link}
          />
          <div className={styles.productMeta}>
            <span>
              {t('INDEX')}: {product.product_basic_short_info?.index}
            </span>
            {product.product_basic_short_info?.producer_name && (
              <span>
                {t('Producent')}: {product.product_basic_short_info?.producer_name}
              </span>
            )}
          </div>
        </div>

        <div className={styles.productActions}>
          {/* Select i Counter w jednej linii */}
          <div className={styles.selectAndCounterRow}>
            {renderUnitSelector()}
            {profile?.role !== 'ROLE_OPEN_PROFILE' ? (
              <Counter
                className={styles.quantityCounter}
                onChange={(value) => updateQuantity({ value, unit_id: unitId })}
                onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
                onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
                value={quantity}
                onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
                disabled={
                  isDecrementQuantityLoading ||
                  isIncrementQuantityLoading ||
                  isUpdateQuantityLoading
                }
              />
            ) : (
              <Link
                to={`/login?return_url=${encodeURIComponent(
                  location.pathname + location.search
                )}`}
                className={styles.loginButton}>
                <Trans>zaloguj</Trans>
              </Link>
            )}
          </div>

          {/* Cena i przycisk koszyka w jednej linii */}
          {profile?.role !== 'ROLE_OPEN_PROFILE' && (
            <div className={styles.priceAndCartRow}>
              <div className={styles.priceWrapper}>
                {price ? (
                  <div className={styles.net}>
                    <strong>
                      {price.price_net_formatted} {price.currency}
                    </strong>
                    <span className={styles.nettoLabel}>
                      ({t('netto')})
                    </span>
                  </div>
                ) : (
                  <div className={styles.net}>
                    <strong>-</strong>
                  </div>
                )}
              </div>
              <div className={styles.addToCartWrapper}>
                <AddToCartButton
                  isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
                  updateQuantity={async () => {
                    const newQuantity = manuallyChangedQuantity
                      ? await updateQuantityAsync({
                          value: manuallyChangedQuantity,
                          unit_id: unitId
                        })
                      : undefined;

                    return newQuantity?.data.value === manuallyChangedQuantity
                      ? manuallyChangedQuantity
                      : undefined;
                  }}
                  quantity={quantity}
                  unitId={unitId}
                  productId={product.id}
                  disabled={
                    isDecrementQuantityLoading ||
                    isIncrementQuantityLoading
                  }
                />
              </div>
            </div>
          )}

          {profile?.role !== 'ROLE_OPEN_PROFILE' && (
            <div className={styles.shoppingListButtonWrapper}>
              <AddToShoppingListButton
                product_id={product.id}
                unit_id={product.product_unit_short_info?.default_unit_id}
                image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
                title={product.product_basic_short_info?.title}
                index={product.product_basic_short_info?.index}
                currency={price?.currency}
                unit={price}
                quantity={quantity}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductItem;
